// O modelo AUTORAL do caça-palavras — a fonte da verdade do editor.
//
// É plano, imutável e serializável em JSON (contrato do shell: history, dirty
// tracking e autosave saem disso de graça). O `wire.ts` converte para o blob
// `crossword-compiler/word-search` que a API grava.
//
// As LETRAS moram em `letters`: a grade é a fonte da verdade do que está escrito.
// `words` guarda só posição + dica. Isso evita que grade e palavra discordem — o
// risco W1 do spec (jogador honesto marcado errado).

import type { NewPuzzleParams } from '../../types';

/** Uma palavra colocada na grade. Coordenadas 1-indexadas, como no blob. */
export interface WordEntry {
  ref: string; // id temporário do cliente
  solution: string; // MAIÚSCULA, sem acento
  clue: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface WordsearchDoc {
  titulo: string;
  width: number;
  height: number;
  letters: string[]; // row-major, width*height, '' = vazia
  words: WordEntry[];
}

/** Índice no array `letters` a partir de coordenada 1-indexada. */
export function cellIndex(x: number, y: number, width: number): number {
  return (y - 1) * width + (x - 1);
}

/** MAIÚSCULA sem acento, só A–Z — a forma que o servidor valida. */
export function normalizeSolution(raw: string): string {
  return raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
}

/**
 * As células que a palavra ocupa, do início ao fim. Aceita H, V e as duas
 * diagonais, em qualquer sentido. Reta inválida (nem H, nem V, nem 45°) → `[]`,
 * que o `validate` traduz em erro.
 */
export function lineCells(word: WordEntry): Array<{ x: number; y: number }> {
  const dx = Math.sign(word.x2 - word.x1);
  const dy = Math.sign(word.y2 - word.y1);
  const spanX = Math.abs(word.x2 - word.x1);
  const spanY = Math.abs(word.y2 - word.y1);

  const straight = spanX === 0 || spanY === 0 || spanX === spanY;
  if (!straight) return [];

  const steps = Math.max(spanX, spanY);
  const cells: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= steps; i++) {
    cells.push({ x: word.x1 + dx * i, y: word.y1 + dy * i });
  }
  return cells;
}

/** Documento vazio — o par de `load()` no fluxo de criação. */
export function createEmpty(params: NewPuzzleParams): WordsearchDoc {
  return {
    titulo: params.titulo,
    width: params.largura,
    height: params.altura,
    letters: new Array(params.largura * params.altura).fill(''),
    words: [],
  };
}
